/**
 * @namespace helpers
 */

/**
 * Helpers for the Skip Intro / Skip Credits content feature (MW-12866 / MW-12965).
 *
 * The backend stores each segment as `{ enabled, showButtonFrom, showButtonUntil,
 * segmentEnd }` with "HH:MM:SS" duration strings (or nulls when disabled). The
 * forms edit them through uib-timepicker, which binds to `Date` objects, so the
 * models keep a `$`-prefixed UI mirror of `Date`s and convert at load/save time.
 *
 * @module skipIntroCredits
 * @memberof helpers
 * @see helpers
 */

import { durationStringToDate, dateToDurationString } from './date'

/**
 * Backend field descriptor for a skip segment — spread into a model `_fields`.
 *
 * @function
 * @return {Object}
 */
export const skipSegmentFields = () => ({
  enabled: Boolean,
  showButtonFrom: String,
  showButtonUntil: String,
  segmentEnd: String
})

/**
 * UI mirror descriptor (`$skipIntro` / `$skipCredits`) — Date pickers + toggle.
 *
 * @function
 * @return {Object}
 */
export const skipSegmentMirrorFields = () => ({
  enabled: Boolean,
  segmentEnd: Date,
  showButtonFrom: Date,
  showButtonUntil: Date
})

/**
 * Build the `$` Date mirror from the backend segment object (called in `_prepare`).
 * Missing/null segment → disabled with zeroed pickers.
 *
 * @function
 * @param  {Object} [segment]
 * @return {Object} mirror
 */
export const prepareSkipSegment = (segment) => ({
  enabled: !!(segment && segment.enabled),
  segmentEnd: durationStringToDate(segment && segment.segmentEnd),
  showButtonFrom: durationStringToDate(segment && segment.showButtonFrom),
  showButtonUntil: durationStringToDate(segment && segment.showButtonUntil)
})

/**
 * Serialize the `$` Date mirror back to the backend segment object (called in
 * `_object`). A disabled segment is sent as `{enabled:false, …:null}` (NOT the
 * whole object as `null`, which the backend ignores — so disabling would not
 * persist).
 *
 * @function
 * @param  {Object} [mirror]
 * @return {Object} backend segment
 */
export const serializeSkipSegment = (mirror) => {
  if (!mirror || !mirror.enabled) {
    return { enabled: false, showButtonFrom: null, showButtonUntil: null, segmentEnd: null }
  }
  return {
    enabled: true,
    showButtonFrom: dateToDurationString(mirror.showButtonFrom),
    showButtonUntil: dateToDurationString(mirror.showButtonUntil),
    segmentEnd: dateToDurationString(mirror.segmentEnd)
  }
}

/**
 * Whole seconds of a Date's time-of-day (a skip value is a content offset).
 *
 * @function
 * @param  {Date} [date]
 * @return {number}
 */
export const dateToSeconds = (date) =>
  date ? date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds() : 0

/**
 * Per-field validation for one enabled segment, against the content length.
 * A value of 0 (00:00:00) is treated as "not set" for the required checks —
 * consistent with how the content Length field itself treats 00:00:00.
 *
 * Each leaf boolean is TRUE when that error is present.
 *
 * @function
 * @param  {Object} segment - `$` Date mirror
 * @param  {number} lengthSecs - content duration in seconds (0 = unknown)
 * @return {Object}
 */
export const evaluateSegment = (segment, lengthSecs) => {
  const segEnd = dateToSeconds(segment.segmentEnd)
  const from = dateToSeconds(segment.showButtonFrom)
  const until = dateToSeconds(segment.showButtonUntil)
  return {
    segmentEnd: {
      required: segEnd <= 0,
      withinDuration: lengthSecs > 0 && segEnd > 0 && segEnd >= lengthSecs
    },
    showButtonFrom: {
      required: from <= 0,
      // start < end and start ≠ end (equal is covered by >=)
      beforeUntil: from > 0 && until > 0 && from >= until,
      beforeSegmentEnd: from > 0 && segEnd > 0 && from >= segEnd
    },
    showButtonUntil: {
      required: until <= 0,
      afterFrom: from > 0 && until > 0 && until <= from,
      withinSegmentEnd: until > 0 && segEnd > 0 && until > segEnd
    }
  }
}

const segmentHasError = (seg) =>
  Object.keys(seg).some((field) =>
    Object.keys(seg[field]).some((rule) => seg[field][rule]))

/**
 * Evaluate both segments plus the cross-segment rules.
 *
 * Cross rules (only when BOTH segments are enabled):
 *  - Intro segment end must be earlier than Credits segment end (and differ).
 *  - The two button display periods must not overlap or share a boundary
 *    (only one playback control can be shown at a time — UI-3.7).
 *
 * @function
 * @param  {Object} params
 * @param  {Object} params.intro - `$skipIntro` mirror
 * @param  {Object} params.credits - `$skipCredits` mirror
 * @param  {Date}   [params.contentLength] - content duration (lengthISO)
 * @return {Object} { valid, intro, credits, overlap }
 */
export const evaluateSkipSegments = ({ intro, credits, contentLength }) => {
  const lengthSecs = dateToSeconds(contentLength)
  const introEnabled = !!(intro && intro.enabled)
  const creditsEnabled = !!(credits && credits.enabled)

  const introResult = introEnabled
    ? evaluateSegment(intro, lengthSecs)
    : evaluateSegment({}, 0)
  const creditsResult = creditsEnabled
    ? evaluateSegment(credits, lengthSecs)
    : evaluateSegment({}, 0)

  // Cross-segment: Intro end earlier than Credits end (surfaced on Credits).
  creditsResult.segmentEnd.afterIntro = false
  let overlap = false
  if (introEnabled && creditsEnabled) {
    const introEnd = dateToSeconds(intro.segmentEnd)
    const creditsEnd = dateToSeconds(credits.segmentEnd)
    if (introEnd > 0 && creditsEnd > 0 && introEnd >= creditsEnd) {
      creditsResult.segmentEnd.afterIntro = true
    }

    const introFrom = dateToSeconds(intro.showButtonFrom)
    const introUntil = dateToSeconds(intro.showButtonUntil)
    const creditsFrom = dateToSeconds(credits.showButtonFrom)
    const creditsUntil = dateToSeconds(credits.showButtonUntil)
    // valid (non-empty, ordered) intervals only — overlap OR touching boundary
    if (introFrom > 0 && introUntil > introFrom && creditsFrom > 0 && creditsUntil > creditsFrom) {
      overlap = introFrom <= creditsUntil && creditsFrom <= introUntil
    }
  }

  const valid =
    !(introEnabled && segmentHasError(introResult)) &&
    !(creditsEnabled && (segmentHasError(creditsResult) || creditsResult.segmentEnd.afterIntro)) &&
    !overlap

  return { valid, intro: introResult, credits: creditsResult, overlap }
}
